import { parseISO } from 'date-fns'
import moment from 'moment'
import { createContext, useContext, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FaPen, FaPrint, FaRegFilePdf } from 'react-icons/fa'
import { FaArrowsRotate, FaX } from 'react-icons/fa6'
import { MdOutlinePayment } from 'react-icons/md'
import { Tooltip } from 'react-tooltip'

import {
  HomeModernIcon,
  ReceiptPercentIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'

import Toggle from '@/components/forms/Toggle'

import useServer from '../../../../api/useServerMain'
import { ModalAlert } from '../../../../components'
import Input from '../../../../components/forms/Input'
import Select from '../../../../components/forms/Select'
import OrderStatusBadge from '../../../../components/misc/badges/OrderStatusBadge'
import Button from '../../../../components/misc/Button'
import Modal from '../../../../components/misc/GenericModal'
import MultipleActBtn from '../../../../components/misc/MultipleActBtn'
import { SelectInterface } from '../../../../interfaces/InterfacesLocal'
import {
  CurrencyInterface,
  KeyNomenclator,
  PriceInvoiceInterface,
  RegisterBillingInterface,
  SimplePrice,
} from '../../../../interfaces/ServerInterfaces'
import BillingReportPdf from '../../../../reports/BillingReportPDF'
import { useAppSelector } from '../../../../store/hooks'
import { RootState } from '../../../../store/root'
import {
  formatCalendar,
  formatCurrency,
  generatePdf,
  printPdf,
} from '../../../../utils/helpers'
// import { PrintTicket } from '../../../../utils/PrintTicket'
import { PrinterUtils, type PaperWidth } from '../../../../utils/PrinterUtils'
import PrintDialog from '../../../../components/misc/PrintDialog'
import { translatePaymetMethods } from '../../../../utils/translate'
import { ConvertToOrderModal } from '../../../store/orders/ConvertToOrderModal'
import { RegisterContext } from '../AllRegistersList'
import { RegisterDetailsContext } from '../RegisterDetailsContainer'
import { WizzardRegisterBilling } from '../registers/WizzardRegisterBilling'
import PaymentContainerV2 from './payment/PaymentContainerV2'

// Product interfaces for edit mode
interface ProductItem {
  id: number
  productId?: number
  product?: {
    id: number
  }
}

interface ExchangeRateData {
  codeCurrency: string
  exchangeRate: number
  printToMainCurrency: boolean
}

interface ShowExchangeRangeState {
  state: boolean
  action: ((_data: ExchangeRateData) => void) | null
}

interface EditWizzardState {
  editMode: boolean
  defaultValues: RegisterBillingInterface | null
}

interface EditContextBillingInterface {
  defaultValues?: RegisterBillingInterface | null
  editMode?: boolean | null
  addedProductsArray?: ProductItem[]
  deletedProductsArray?: ProductItem[]
  addProductsToAddArray?: (_products: ProductItem) => void
  delProductToAddArray?: (_productId: number) => void
  addProductsToDelArray?: (_products: ProductItem) => void
  delProductToDelArray?: (_productId: number) => void
  setEditWizzard?: (_state: EditWizzardState) => void
  setAddedProductsArray?: (_products: ProductItem[]) => void
  setDeletedProductsArray?: (_products: ProductItem[]) => void
}

export const EditContextBilling = createContext<
  Partial<EditContextBillingInterface>
>({})

const RegisterDetailsTab = () => {
  const { denyRoles } = useServer()

  const { business } = useAppSelector((state: RootState) => state.init)
  const {
    cancelOrder,
    reset,
    clearArrayOfProducts,
    setCurrentStep,
    isFetching,
    refundBillingOrder,
    openPayModal,
    setOpenPayModal,
  } = useContext(RegisterContext)

  const { handleSubmit, control, watch } = useForm()

  //-----------Control open payment modal with register and billing --->
  const [paymentModal, setPaymentModal] = useState(false)
  const { calculatePaymentDiff } = useServer()

  useEffect(() => {
    if (openPayModal) {
      setPaymentModal(true)
    }
  }, [openPayModal])
  useEffect(() => {
    if (!paymentModal) {
      setOpenPayModal?.(false)
    }
  }, [paymentModal, setOpenPayModal])
  //-----------Control open payment modal with register and billing --->
  const {
    order,
    closeModalDetails,
    deletePartialPayment,
    updateAllOrdersState,
    updateSingleOrderState,
    TransformPreReceiptIntoReceipt,
  } = useContext(RegisterDetailsContext)

  const difference = calculatePaymentDiff(order?.totalToPay, [
    ...(order?.partialPayments ?? []),
  ])

  const [editWizzard, setEditWizzard] = useState<EditWizzardState>({
    editMode: false,
    defaultValues: null,
  })
  const [cancelModal, setCancelModal] = useState(false)
  const [convertModal, setConvertModal] = useState(false)
  const [convertToOrderWithAreaModal, setConvertToOrderWithAreaModal] =
    useState(false)
  const [refundModal, setRefundModal] = useState(false)
  const [showExchangeRange, setshowExchangeRange] =
    useState<ShowExchangeRangeState>({
      state: false,
      action: null,
    })
  const [showPrintDialog, setShowPrintDialog] = useState(false)

  // Función para imprimir ticket con diálogo de selección de ancho
  const handlePrintTicket = async (paperWidth: PaperWidth) => {
    if (business && order) {
      await PrinterUtils.printTicketWithDialog(order, business, paperWidth)
    }
  }

  // EDIT MODE =================
  const [addedProductsArray, setAddedProductsArray] = useState<ProductItem[]>(
    []
  )
  const [deletedProductsArray, setDeletedProductsArray] = useState<
    ProductItem[]
  >([])

  const addProductsToAddArray = (products: ProductItem) => {
    setAddedProductsArray([...addedProductsArray, products])
  }

  const delProductToAddArray = (productId: number) => {
    setAddedProductsArray(
      addedProductsArray.filter(
        (item: ProductItem) => item.productId ?? item.product?.id !== productId
      )
    )
  }

  const addProductsToDelArray = (products: ProductItem) => {
    setDeletedProductsArray([...deletedProductsArray, products])
  }

  const delProductToDelArray = (productId: number) => {
    setDeletedProductsArray(
      deletedProductsArray.filter(
        (product: ProductItem) => product.id !== productId
      )
    )
  }

  const contextEditValues = {
    defaultValues: editWizzard.defaultValues,
    editMode: editWizzard.editMode,
    addedProductsArray,
    deletedProductsArray,
    addProductsToAddArray,
    delProductToAddArray,
    addProductsToDelArray,
    delProductToDelArray,
    setEditWizzard,
    setDeletedProductsArray,
    setAddedProductsArray,
  }

  // actions
  const actions = []
  const prints = []
  const exports = []

  if (!order?.isPreReceipt) {
    if (order?.status !== 'REFUNDED') {
      exports.push(
        {
          title: 'Exportar albarán',
          icon: <FaRegFilePdf className="h-5 text-gray-500" />,
          action: () => {
            generatePdf(
              BillingReportPdf({ order, business, reportType: 'delivery' }),
              'Albarán'
            )
          },
        },
        {
          title: 'Exportar albarán valorado',
          icon: <FaRegFilePdf className="h-5 text-gray-500" />,
          action: () => {
            generatePdf(
              BillingReportPdf({
                order,
                business,
                reportType: 'delivery_valued',
              }),
              'Albarán valorado'
            )
          },
        }
      )
      prints.push(
        {
          title: 'Imprimir albarán',
          icon: <FaPrint className="h-5 text-gray-500" />,
          action: () => {
            printPdf(
              BillingReportPdf({ order, business, reportType: 'delivery' }),
              'Albarán'
            )
          },
        },
        {
          title: 'Imprimir albarán valorado',
          icon: <FaPrint className="h-5 text-gray-500" />,
          action: () => {
            printPdf(
              BillingReportPdf({
                order,
                business,
                reportType: 'delivery_valued',
              }),
              'Albarán valorado'
            )
          },
        }
      )
    }
    exports.push(
      {
        title: 'Exportar factura',
        icon: <FaRegFilePdf className="h-5 text-gray-500" />,
        action: () => {
          generatePdf(
            BillingReportPdf({ order, business, reportType: 'billing' }),
            'Factura'
          )
        },
      },
      {
        title: 'Exportar factura a tasa de cambio',
        icon: <FaRegFilePdf className="h-5 text-gray-500" />,
        action: () => {
          setshowExchangeRange({
            state: true,
            action: (data: ExchangeRateData) => {
              generatePdf(
                BillingReportPdf({
                  order,
                  business,
                  reportType: 'billing',
                  exportAtExchangeRate: data,
                  printToMainCurrency: data.printToMainCurrency,
                }),
                'Factura'
              )
            },
          })
        },
      }
    )
    prints.push(
      {
        title: 'Imprimir factura',
        icon: <FaPrint className="h-5 text-gray-500" />,
        action: () => {
          printPdf(
            BillingReportPdf({ order, business, reportType: 'billing' }),
            'Factura'
          )
        },
      },
      {
        title: 'Imprimir factura a tasa de cambio',
        icon: <FaPrint className="h-5 text-gray-500" />,
        action: () => {
          setshowExchangeRange({
            state: true,
            action: (data: ExchangeRateData) => {
              printPdf(
                BillingReportPdf({
                  order,
                  business,
                  reportType: 'billing',
                  exportAtExchangeRate: data,
                  printToMainCurrency: data.printToMainCurrency,
                }),
                'Factura'
              )
            },
          })
        },
      },
      {
        title: 'Imprimir ticket',
        icon: <FaPrint className="h-5 text-gray-500" />,
        action: () => {
          setShowPrintDialog(true)
        },
      }
    )
  }

  if (order?.status === 'BILLED' && denyRoles(['AUDITOR'])) {
    actions.push({
      title: 'Cancelar',
      icon: <FaX className="h-4 text-gray-500" />,
      action: () => setCancelModal(true),
    })
  }

  if (
    order?.status !== 'BILLED' &&
    !order?.isPreReceipt &&
    denyRoles(['AUDITOR'])
  ) {
    if (order?.status !== 'CANCELLED') {
      if (order?.status !== 'REFUNDED') {
        actions.push({
          title: 'Registro de pago',
          icon: <MdOutlinePayment className="text-xl text-gray-500" />,
          action: () => setPaymentModal(true),
        })
      }
    }
    if (
      !['BILLED', 'REFUNDED', 'CANCELLED', 'RECEIVED'].includes(
        order?.status ?? ''
      )
    ) {
      actions.push({
        title: 'Editar',
        icon: <FaPen className="h-5 text-gray-500" />,
        action: () => {
          setEditWizzard({ editMode: true, defaultValues: order ?? null })
        },
      })
      actions.push({
        title: 'Cancelar',
        icon: <FaX className="h-4 text-gray-500" />,
        action: () => setCancelModal(true),
      })
    }
  }

  const convertToBillFunction = () => {
    if (order?.areaSales === null) {
      setConvertToOrderWithAreaModal(true)
    } else {
      setConvertModal(true)
    }
  }

  const editPreBilling = ['CREATED', 'PAYMENT_PENDING', 'IN_PROCESS']

  // PRE_BILLING
  if (order?.isPreReceipt) {
    if (editPreBilling.includes(order.status)) {
      actions.push(
        {
          title: 'Exportar Pre-Factura',
          icon: <FaRegFilePdf className="h-5 text-gray-500" />,
          action: () => {
            // setExportPdfModal({ state: true, type: "Pre-Factura" });
            generatePdf(
              BillingReportPdf({ order, business, reportType: 'prebilling' }),
              'Pre-Factura'
            )
          },
        },
        {
          title: 'Imprimir Pre-Factura',
          icon: <FaPrint className="h-5 text-gray-500" />,
          action: () => {
            // setExportPdfModal({ state: true, type: "Pre-Factura" });
            printPdf(
              BillingReportPdf({ order, business, reportType: 'prebilling' }),
              'Pre-Factura'
            )
          },
        },
        {
          title: 'Editar',
          icon: <FaPen className="h-5 text-gray-500" />,
          action: () => {
            setEditWizzard({ editMode: true, defaultValues: order ?? null })
          },
        }
      )
    }
    if (order.status !== 'CANCELLED') {
      actions.push(
        {
          title: 'Convertir a factura',
          icon: <FaArrowsRotate className="h-5 text-gray-500" />,
          action: () => convertToBillFunction(),
        },
        {
          title: 'Cancelar',
          icon: <FaX className="h-4 text-gray-500" />,
          action: () => setCancelModal(true),
        }
      )
    }
  }

  if (order?.status === 'CANCELLED') {
    actions.length = 0
    actions.push({
      title: 'Exportar factura',
      icon: <FaRegFilePdf className="h-5 text-gray-500" />,
      action: () => {
        generatePdf(
          BillingReportPdf({ order, business, reportType: 'billing' }),
          'Factura'
        )
      },
    })
  }

  const closeWizzardInEditModeModal = () => {
    setCurrentStep?.(0)
    setEditWizzard({ editMode: false, defaultValues: null })
    //closeModalDetails!();
    clearArrayOfProducts?.([])
    reset?.()
  }

  const totalPartialPay: {
    amount: number
    codeCurrency: string
    paymentWay?: string
  }[] = []

  if (order?.status !== 'BILLED') {
    if (order?.partialPayments && order?.partialPayments?.length > 0) {
      for (const item of order?.partialPayments ?? []) {
        const found = totalPartialPay.find(
          (data) => data.codeCurrency === item.codeCurrency
        )

        if (found) {
          found.amount += item.amount
        } else {
          totalPartialPay.push({
            amount: item.amount,
            codeCurrency: item.codeCurrency,
            paymentWay: item?.paymentWay,
          })
        }
      }
    }
  } else {
    totalPartialPay.push(...order.currenciesPayment)
  }

  const statusNotPay = ['CREATED', 'PAYMENT_PENDING', 'OVERDUE']
  const totalToPay: SimplePrice[] = statusNotPay.includes(order?.status || '')
    ? [...(difference as SimplePrice[])]
    : [...(order?.totalToPay || [])]

  const amountReturn = order?.amountReturned
  const houseCosted = order?.houseCosted

  const discount: PriceInvoiceInterface[] = []
  if (order?.couponDiscountPrice || order?.discount !== 0) {
    if (order?.couponDiscountPrice) discount.push(order?.couponDiscountPrice)
    if (order?.discount !== 0) {
      order?.prices.forEach((item) => {
        const amount = (order.discount / 100) * item.price
        const idx = discount.findIndex(
          (elem) => elem.codeCurrency === item.codeCurrency
        )
        if (idx !== -1) {
          discount.splice(idx, 1, {
            ...discount[idx],
            amount: discount[idx].amount + amount,
          })
        } else {
          discount.push({
            amount: amount,
            codeCurrency: item.codeCurrency || '',
          })
        }
      })
    }
  }

  const comission: PriceInvoiceInterface[] = []
  if (order?.commission && order?.commission > 0) {
    order?.prices.forEach((item) => {
      const amount = (order.commission / 100) * item.price
      const idx = comission.findIndex(
        (elem) => elem.codeCurrency === item.codeCurrency
      )
      if (idx !== -1) {
        comission.splice(idx, 1, {
          ...comission[idx],
          amount: comission[idx]?.amount + amount,
        })
      } else {
        comission.push({
          amount: amount,
          codeCurrency: item.codeCurrency || '',
        })
      }
    })
  }

  const currencySelector: SelectInterface[] =
    business?.availableCurrencies.map((item: CurrencyInterface) => ({
      id: item.code,
      name: item.code,
    })) ?? []

  const onSubmit: SubmitHandler<Record<string, unknown>> = (data) => {
    if (showExchangeRange.action !== null) {
      showExchangeRange.action(data as unknown as ExchangeRateData)
      setshowExchangeRange({ state: false, action: null })
    }
  }

  const billing_print_force_currency = business?.configurationsKey?.find(
    (configuration: KeyNomenclator) =>
      configuration.key === 'billing_print_force_currency'
  )?.value

  /* const billing_print_force_currency_defined =
    business?.configurationsKey?.find(
      (configuration: { key: string }) =>
        configuration.key === 'billing_print_force_currency_defined'
    )?.value */

  const billing_show_total_payment_gateway = business?.configurationsKey?.find(
    (configuration: KeyNomenclator) =>
      configuration.key === 'billing_show_total_payment_gateway'
  )?.value

  return (
    <div className="h-[26rem] overflow-y-auto px-3 scrollbar-thin scrollbar-thumb-gray-300">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center justify-start gap-4 text-xl font-semibold text-gray-700">
          {/* {order?.isPreReceipt
            ? ` 
            Pre-Factura No.${order?.preOperationNumber}/${parseISO(
              order?.createdAt!
            ).getFullYear()}`
            : `Factura No.${order?.operationNumber ?? ""}/${parseISO(
              order?.createdAt!
            ).getFullYear()}`} */}

          {`${
            order?.name
              ? `${order.name}.${
                  order?.isPreReceipt
                    ? order?.preOperationNumber
                    : order?.operationNumber
                }`
              : `${
                  order?.isPreReceipt
                    ? `Pre-Factura No.${order?.preOperationNumber}`
                    : `Factura No.${order?.operationNumber}`
                }/${parseISO(order?.createdAt ?? '').getFullYear()}`
          }`}

          {/* <h5 className="text-lg font-medium text-gray-900 inline-flex items-center gap-10">
              Orden: #{order?.operationNumber}{" "}
              {order?.isForTakeAway ? `` : order?.name} */}
          {order?.isForTakeAway && (
            <span className="">
              <TruckIcon
                className="z-30 h-5 text-gray-900"
                data-tooltip-content="Para llevar"
                data-tooltip-id="my-tooltip"
              />
            </span>
          )}
          {order?.houseCosted && (
            <span className="">
              <HomeModernIcon
                className="z-30 h-5 text-green-600"
                data-tooltip-content="Consumo casa"
                data-tooltip-id="my-tooltip"
              />
            </span>
          )}
          {order?.discount === 100 && (
            <span className="">
              <ReceiptPercentIcon
                className="h-5 text-gray-900"
                data-tooltip-content="Descuento 100%"
                data-tooltip-id="my-tooltip"
              />
            </span>
          )}
          <Tooltip id="my-tooltip" place="top" />
          {/* </h5> */}
        </h3>
        <div className="relative inline-flex items-center gap-2">
          <div className="fixed right-16 flex items-center gap-2">
            {prints.length > 0 && (
              <div className="">
                {' '}
                <MultipleActBtn btnName="Imprimir" items={prints} />
              </div>
            )}
            {exports.length > 0 && (
              <div className="">
                <MultipleActBtn btnName="Exportar" items={exports} />
              </div>
            )}
            {actions.length > 0 && (
              <div className="">
                <MultipleActBtn btnName="Acciones" items={actions} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-2">
        {!order?.isPreReceipt && order?.preOperationNumber && (
          <div className="inline-flex gap-2">
            <p className="font-semibold text-gray-600">No. de prefactura:</p>
            <p className="text-gray-500">
              {moment(order?.createdAt).get('year')}/{order.preOperationNumber}
            </p>
          </div>
        )}
        {order?.name && (
          <div className="inline-flex gap-2">
            <p className="font-semibold text-gray-600">Nombre:</p>
            <p className="text-gray-500">{order?.name}</p>
          </div>
        )}

        <div className="inline-flex gap-2">
          <p className="font-semibold text-gray-600">Fecha de creación:</p>
          <p className="text-gray-500">{formatCalendar(order?.createdAt)}</p>
        </div>
        {order?.paymentDeadlineAt && (
          <div className="inline-flex gap-2">
            <p className="font-semibold text-gray-600">Fecha de vencimiento:</p>
            <p className="text-gray-500">
              {moment(order?.paymentDeadlineAt).format('YYYY-MM-DD')}
            </p>
          </div>
        )}
        <div className="inline-flex gap-2">
          <p className="font-semibold text-gray-600">Pagado:</p>
          <p className="text-gray-500">
            {order?.paidAt ? formatCalendar(order.paidAt) : '-'}
          </p>
        </div>
        <div className="inline-flex w-full items-center gap-5">
          <p className="font-semibold text-gray-600">Estado:</p>
          <OrderStatusBadge status={order?.status} />
        </div>
        <div className="inline-flex gap-2">
          <p className="font-semibold text-gray-600">Cliente:</p>
          <p className="text-gray-500">
            {order?.client?.firstName || order?.client?.lastName
              ? `${order?.client?.firstName ?? ''} ${
                  order?.client?.lastName ?? ''
                }`
              : (order?.client?.email ?? 'Invitado')}
          </p>
        </div>
        {order?.client?.codeClient && (
          <div className="inline-flex gap-2">
            <p className="font-semibold text-gray-600">Código de cliente: </p>
            <p className="text-gray-500">C-{order?.client?.codeClient ?? ''}</p>
          </div>
        )}
        {order?.client?.contractNumber && (
          <div className="inline-flex gap-2">
            <p className="font-semibold text-gray-600">No. contrato: </p>
            <p className="text-gray-500">
              NC-{moment(order?.client.createdAt).year()}/
              {order?.client?.contractNumber ?? ''}
            </p>
          </div>
        )}
        {order?.managedBy && (
          <div className="inline-flex gap-2">
            <p className="font-semibold text-gray-600">Comercial: </p>
            <p className="text-gray-500">
              {order?.managedBy?.username ?? ''}/
              {order?.managedBy?.displayName ?? ''}
            </p>
          </div>
        )}
      </div>

      {/*Cupones*/}
      {order?.coupons?.length && order?.coupons?.length > 0 && (
        <div className="mt-2 flex flex-row gap-1">
          <h5 className="text-md font-semibold">Cupones:</h5>
          {/* @ts-ignone */}
          {order?.coupons
            .map((coupon) => {
              return coupon.code
            })
            .join(', ')}
        </div>
      )}

      <footer className="">
        <article className="grid grid-cols-1">
          {totalToPay && !houseCosted && (
            <article className="flex flex-col pt-3">
              <p className="font-semibold text-gray-600">Total a pagar:</p>
              {totalToPay?.map((item: SimplePrice) => {
                return (
                  <p
                    className="text-wrap break-words text-sm text-gray-500"
                    key={item.codeCurrency}
                  >
                    {formatCurrency(Math.abs(item.amount), item.codeCurrency)}
                  </p>
                )
              })}
            </article>
          )}

          {totalPartialPay.length > 0 && !houseCosted && (
            <article className="flex flex-col pt-3">
              <p className="font-semibold text-gray-600">Total pagado:</p>
              {totalPartialPay.map((item) => {
                return (
                  <p
                    className="text-wrap break-words text-sm text-gray-500"
                    key={item.codeCurrency}
                  >
                    {`${
                      item.paymentWay === 'TRANSFER'
                        ? 'T'
                        : item.paymentWay === 'CASH'
                          ? 'E'
                          : ''
                    } ${formatCurrency(item.amount, item.codeCurrency)}`}
                    <span className="ml-2">
                      {' '}
                      {billing_show_total_payment_gateway === 'true' &&
                        translatePaymetMethods(item.paymentWay)}
                    </span>
                  </p>
                )
              })}
            </article>
          )}

          {/* Descuentos */}
          {order?.discount && !houseCosted ? (
            <article className="flex flex-col pt-3">
              <p className="font-semibold text-gray-600">
                Descuento:{' '}
                <p className="text-wrap break-words text-sm text-gray-500">
                  {order.discount}%
                </p>
              </p>
              <span>
                {discount.map((item) => {
                  return (
                    <p
                      className="text-wrap break-words text-sm text-gray-500"
                      key={item.codeCurrency}
                    >
                      {formatCurrency(item.amount, item.codeCurrency)}
                    </p>
                  )
                })}
              </span>
            </article>
          ) : (
            ''
          )}
          {/* Comisiones */}
          {order?.commission && !houseCosted ? (
            <article className="flex flex-col pt-3">
              <p className="font-semibold text-gray-600">
                Comisiones:{' '}
                <p className="text-wrap break-words text-sm text-gray-500">
                  {order.commission}%
                </p>
              </p>
              {comission.map((item) => {
                return (
                  <p
                    className="text-wrap break-words text-sm text-gray-500"
                    key={item.codeCurrency}
                  >
                    {formatCurrency(item.amount, item.codeCurrency)}
                  </p>
                )
              })}
            </article>
          ) : (
            ''
          )}

          {amountReturn && !houseCosted && amountReturn?.amount > 0 && (
            <article className="flex flex-col pt-3">
              <p className="font-semibold text-gray-600">Cambio:</p>
              <p className="text-wrap break-words text-sm text-gray-500">
                {formatCurrency(amountReturn.amount, amountReturn.codeCurrency)}
              </p>
            </article>
          )}
        </article>

        <aside className="grid grid-cols-2">
          {order?.customerNote && (
            <div className="flex flex-col pt-3">
              <p className="font-semibold text-gray-600">
                Nota ofrecida por el cliente:
              </p>
              <div className="p-2">
                <p className="text-wrap break-words text-gray-500">
                  {order?.customerNote}
                </p>
              </div>
            </div>
          )}
          {order?.observations && (
            <div className="flex flex-col pt-3">
              <p className="mt-2 text-sm font-semibold text-gray-600">
                Observaciones:
              </p>
              <div className="p-2">
                <p className="text-wrap break-words text-gray-500">
                  {order?.observations}
                </p>
              </div>
            </div>
          )}
        </aside>
      </footer>

      {showExchangeRange.state && (
        <Modal
          close={() => setshowExchangeRange({ state: false, action: null })}
          state={showExchangeRange.state}
        >
          <form
            className="flex w-full flex-col gap-3"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex w-full items-center justify-between">
              <Select
                control={control}
                data={currencySelector.filter(
                  (item) => item.name !== business?.mainCurrency
                )}
                label="Moneda"
                name="codeCurrency"
                rules={{ required: 'Requerido' }}
              />

              <p className="mx-4 mt-6 text-xl font-semibold">=</p>

              <Input
                control={control}
                label="Cambio"
                name="exchangeRate"
                rules={{ required: 'Requerido' }}
                type="number"
              />

              <p className="mx-4 mt-6 text-xl">{business?.mainCurrency}</p>
            </div>
            <div>
              <Toggle
                control={control}
                name={'printToMainCurrency'}
                title={'Exportar reporte en moneda principal'}
              />
            </div>

            {billing_print_force_currency === 'true' && (
              <div>
                <p className="text-red-500">
                  La propiedad &quot;Forzar impresión de ticket/factura en
                  moneda definida&quot; no surte efecto en este reporte.
                </p>
                <p className={'mt-2'}>
                  La factura se imprimirá en la moneda seleccionada (
                  <span className={'font-semibold'}>
                    {watch('printToMainCurrency')
                      ? business?.mainCurrency
                      : (watch('codeCurrency') ?? '-')}
                  </span>
                  )
                </p>
              </div>
            )}

            <div className="flex justify-end pt-5">
              <Button name="Aceptar" type="submit" />
            </div>
          </form>
        </Modal>
      )}

      {paymentModal && (
        <Modal close={setPaymentModal} size="l" state={paymentModal}>
          <PaymentContainerV2
            closeModal={() => {
              setPaymentModal(false)
            }}
            dependencies={{
              isFetching,
              order,
              deletePartialPayment:
                deletePartialPayment ??
                (() => {
                  //
                }),
              updateAllOrdersState:
                updateAllOrdersState ??
                (() => {
                  //
                }),
              updateSingleOrderState:
                updateSingleOrderState ??
                (() => {
                  //
                }),
            }}
          />
        </Modal>
      )}
      {editWizzard.editMode && (
        <Modal
          close={() => closeWizzardInEditModeModal()}
          size="l"
          state={editWizzard.editMode}
        >
          <EditContextBilling.Provider value={contextEditValues}>
            <WizzardRegisterBilling
              close={() => closeWizzardInEditModeModal()}
              defaultValues={editWizzard.defaultValues}
              editMode={editWizzard.editMode}
            />
          </EditContextBilling.Provider>
        </Modal>
      )}
      {cancelModal && (
        <ModalAlert
          isLoading={isFetching}
          onAccept={async () => {
            await cancelOrder?.(order?.id ?? 0)
            setCancelModal(false)
            closeModalDetails?.()
          }}
          onClose={() => setCancelModal(false)}
          text={`¿Está seguro de querer cancelar esta ${
            order?.isPreReceipt ? 'pre-factura' : 'factura'
          }? Esta operación no se puede deshacer.`}
          title={`Cancelar ${order?.isPreReceipt ? 'pre-factura' : 'factura'} `}
          type="warning"
        />
      )}
      {convertToOrderWithAreaModal && (
        <Modal
          close={() => setConvertToOrderWithAreaModal(false)}
          state={convertToOrderWithAreaModal}
        >
          <ConvertToOrderModal
            isFetching={isFetching ?? false}
            orderId={order?.id ?? 0}
            setConvertToOrderWithAreaModal={setConvertToOrderWithAreaModal}
            TransformPreReceiptIntoReceipt={
              TransformPreReceiptIntoReceipt ??
              (() => {
                //
              })
            }
          />
        </Modal>
      )}
      {convertModal && (
        <Modal close={() => setConvertModal(false)} state={convertModal}>
          <p>
            ¿Está seguro que desea convertir a factura el pedido #
            {order?.preOperationNumber}? Esta acción es irreversible
          </p>
          <div className="mt-4 flex w-full justify-end">
            <Button
              action={() => {
                TransformPreReceiptIntoReceipt?.(order?.id ?? 0, {}, () =>
                  setConvertModal(false)
                )
              }}
              color="slate-600"
              loading={isFetching}
              name="Aceptar"
              type="button"
            />
          </div>
        </Modal>
      )}
      {refundModal && (
        <ModalAlert
          isLoading={isFetching}
          onAccept={async () => {
            await refundBillingOrder?.(order?.id ?? 0)
            await setCancelModal(false)
            closeModalDetails?.()
          }}
          onClose={() => setRefundModal(false)}
          text={`¿Esta seguro de querer reembolsar esta factura en su totalidad?`}
          title={`Reembolsar factura `}
          type="warning"
        />
      )}

      {/* Diálogo de selección de ancho para impresión */}
      <PrintDialog
        description="Seleccione el ancho del papel para imprimir el ticket"
        onOpenChange={setShowPrintDialog}
        onPrint={handlePrintTicket}
        open={showPrintDialog}
        title="Imprimir Ticket"
      />
    </div>
  )
}

export default RegisterDetailsTab

/*  */
